import { useState, useEffect, useMemo } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ChevronDown, Check, Loader2, Shield, Building2,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react'
import { usePersona } from '@/hooks/usePersona'
import { useAuth } from '@/hooks/useAuth'
import { useSwitchTenant } from '@/hooks/useSwitchTenant'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { isPlatformRole } from '@/types/auth'
import { cn } from '@/utils'
import { Logo } from '@/components/ui/Logo'
import { Wordmark } from '@/components/ui/Wordmark'
import { navigation, exactMatchHrefs, NAV_COLLAPSED_KEY } from './navigation'
import type { NavItem } from './navigation'
import { transition } from '@/lib/motion'
import { useReducedMotion } from '@/hooks/useReducedMotion'

// ── Active-nav marker ────────────────────────────────────────────────────────

// Shared geometry so the animated and static forms cannot drift apart.
// pointer-events-none: purely decorative, and mid-slide these are transformed
// outside their own row's box — they must never sit between the cursor and a
// neighbouring nav item.
const NAV_PILL_CLASS = 'pointer-events-none absolute inset-0 rounded-xl bg-primary-100'
const NAV_BAR_CLASS = 'pointer-events-none absolute left-0 inset-y-1.5 w-1 rounded-r bg-primary-600'

/**
 * The highlight behind the active sidebar row: a filled pill plus the accent
 * bar on its leading edge.
 *
 * Only ONE nav item is active at a time, so both halves are single shared
 * elements addressed by `layoutId`. On navigation the old instance unmounts
 * and the new one mounts, and Framer Motion animates the same element between
 * the two measured boxes — the highlight slides to the new row instead of
 * disappearing here and reappearing there.
 *
 * The transform lands on these two absolutely-positioned spans only, never on
 * the NavLink or any ancestor: a transform creates a containing block for
 * `position: fixed` descendants, which is what broke the click-away backdrops
 * in 1ffa5dff5 (see PageTransition.tsx).
 */
/**
 * One sidebar nav row. Extracted so the pinned footer items (Deleted) render
 * through the EXACT same markup as the scrolling list — same geometry, same
 * active marker, same layoutId. That is what lets the highlight slide between
 * a list item and a footer item rather than snapping when it crosses that
 * boundary. Do not inline a second copy of this.
 */
function SidebarNavLink({
  item,
  showExpanded,
  animateMarker,
}: {
  item: NavItem
  showExpanded: boolean
  animateMarker: boolean
}) {
  // T1.6a: generic end-match rule — use `end` only when another nav item is a
  // child of this path (prevents the parent link from staying active when a
  // child link is matched). Precomputed once in navigation.ts (m6) — no
  // per-render O(n²) scan.
  const useEnd = exactMatchHrefs.has(item.to)

  return (
    <NavLink
      to={item.to}
      end={useEnd}
      aria-label={!showExpanded ? item.name : undefined}
      title={!showExpanded ? item.name : undefined}
      className={({ isActive }) =>
        cn(
          // Focus ring is OUTSET, not `ring-inset`. An inset box-shadow paints
          // above the background but below every descendant, and the active
          // fill is now an opaque descendant span (ActiveNavMarker) covering
          // inset-0 — so an inset ring was swallowed on a row that is both
          // active and keyboard-focused, which is exactly the row a keyboard
          // user lands on when tabbing to the page they are already on.
          // An outset ring is drawn outside the border box, where no
          // descendant can reach it. (a11y review 2026-08-13)
          'relative flex items-center rounded-xl text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          showExpanded ? 'gap-3 px-3 py-2' : 'justify-center px-0 py-2',
          isActive ? 'font-semibold text-primary-700' : 'text-gray-700 hover:bg-gray-100'
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Active marker. Was a static `bg-primary-100` fill plus a `before:`
              left bar on the link itself, which snapped between items. Now two
              shared elements that Framer Motion SLIDES to the newly-active row
              (see HubTabs.tsx for the same technique). Painted first and
              positioned absolutely, with the icon/label marked `relative`, so
              the marker sits behind the content without needing a z-index. */}
          {isActive && <ActiveNavMarker animate={animateMarker} />}
          <item.icon className="relative h-5 w-5 shrink-0" />
          {showExpanded && <span className="relative">{item.name}</span>}
        </>
      )}
    </NavLink>
  )
}

function ActiveNavMarker({ animate }: { animate: boolean }) {
  if (!animate) {
    return (
      <>
        <span aria-hidden="true" className={NAV_PILL_CLASS} />
        <span aria-hidden="true" className={NAV_BAR_CLASS} />
      </>
    )
  }
  return (
    <>
      <motion.span
        aria-hidden="true"
        layoutId="sidebar-active-pill"
        className={NAV_PILL_CLASS}
        transition={transition.normal}
      />
      <motion.span
        aria-hidden="true"
        layoutId="sidebar-active-bar"
        className={NAV_BAR_CLASS}
        transition={transition.normal}
      />
    </>
  )
}

// ── Grouping helpers ─────────────────────────────────────────────────────────

interface NavSubSection {
  name: string | undefined
  items: NavItem[]
}

interface NavSection {
  name: string
  subSections: NavSubSection[]
}

/**
 * Groups a flat NavItem list into sections → sub-sections, preserving order.
 * Items without a section land in an '__ungrouped' bucket rendered without a header.
 */
function groupNavigation(items: NavItem[]): NavSection[] {
  const sections: NavSection[] = []

  for (const item of items) {
    const sectionName = item.section ?? '__ungrouped'
    let section = sections.find((s) => s.name === sectionName)
    if (!section) {
      section = { name: sectionName, subSections: [] }
      sections.push(section)
    }

    const subSectionName = item.subSection
    let subSection = section.subSections.find((ss) => ss.name === subSectionName)
    if (!subSection) {
      subSection = { name: subSectionName, items: [] }
      section.subSections.push(subSection)
    }
    subSection.items.push(item)
  }

  return sections
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Persona-based sidebar that uses capability checks (via usePersona) instead
 * of raw role lists to determine which navigation items are visible.
 *
 * Navigation config is the single source of truth in navigation.ts.
 * Section open/closed state is persisted to localStorage under NAV_COLLAPSED_KEY.
 * Keyboard: Ctrl+B / Cmd+B toggles the sidebar; Enter/Space on section headers
 * toggles collapse (native button behaviour).
 */
export function PersonaSidebar() {
  const persona = usePersona()
  const { user } = useAuth()
  const { tenantMemberships } = useAuthStore()
  const { switchTo, isSwitching } = useSwitchTenant()
  const { sidebarOpen: expanded, toggleSidebar } = useUIStore()
  const navigate = useNavigate()
  const prefersReducedMotion = useReducedMotion()
  const location = useLocation()
  const [switcherOpen, setSwitcherOpen] = useState(false)
  // T2.4: track hover state for responsive rail expand-on-hover.
  // Only triggers visual expansion when the sidebar is in auto-collapsed rail
  // mode (expanded=false). The persistent sidebarOpen state is never changed
  // by hover — Ctrl+B and the toggle button remain the only ways to persist.
  const [hovered, setHovered] = useState(false)

  // Persisted collapse state: { [sectionLabel]: true } means the section is collapsed.
  // Manage starts closed so new users see their daily workspace before specialist tools.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(NAV_COLLAPSED_KEY) ?? '{}') as Record<string, boolean>
      return { Manage: true, ...stored }
    } catch {
      return { Manage: true }
    }
  })

  const toggleSection = (sectionName: string) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [sectionName]: !prev[sectionName] }
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, JSON.stringify(next))
      } catch {
        // localStorage unavailable (private browsing, storage quota) — state
        // remains in memory for this session without being persisted.
      }
      return next
    })
  }

  // Keyboard shortcut: Ctrl+B or Cmd+B to toggle sidebar (same as VS Code / Figma)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        toggleSidebar()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggleSidebar])

  // T2.4: Auto-collapse on screens narrower than xl (1280px) — Workato pattern.
  // Below this width the sidebar collapses to icon rail automatically; hovering
  // it temporarily expands without persisting the state.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1279px)')
    if (mq.matches) useUIStore.getState().setSidebarOpen(false)
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) {
        useUIStore.getState().setSidebarOpen(false)
        setHovered(false)
      }
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const visibleItems = useMemo(() => navigation.filter((item) => item.visible(persona)), [persona])
  // Footer-pinned items (Deleted) are lifted out of the scrolling list and
  // rendered above the collapse control. The RBAC filter above still gates
  // them — this split only decides WHERE a visible item is drawn, never
  // WHETHER it is.
  const listItems = useMemo(() => visibleItems.filter((i) => !i.footer), [visibleItems])
  const footerItems = useMemo(() => visibleItems.filter((i) => i.footer), [visibleItems])
  const sections = useMemo(() => groupNavigation(listItems), [listItems])

  // Never hide the active area after a deep link or refresh.
  useEffect(() => {
    const activeSection = visibleItems.find(
      (item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/')
    )?.section
    if (activeSection) {
      setCollapsedSections((previous) => previous[activeSection] ? { ...previous, [activeSection]: false } : previous)
    }
  }, [location.pathname, visibleItems])

  // The switch flow (reset, cross-role reload, toast) lives in useSwitchTenant,
  // shared with the "not in this organization" state (#301).
  const handleSwitchTenant = (tenantId: string, tenantName: string, targetIsPlatform: boolean) => {
    setSwitcherOpen(false)
    void switchTo({ tenantId, tenantName, isPlatform: targetIsPlatform })
  }

  const currentIsPlatform = user && isPlatformRole(user.role)
  const showSwitcher = tenantMemberships.length > 1

  // Visual expansion = user-toggled OR hover-over rail; layout width never
  // changes on hover (CSS grid transition handles the interior content).
  const showExpanded = expanded || hovered

  return (
    <div
      className={cn(
        // relative z-30 keeps this rail above <main>. NOTE: .beskar-glass is NOT
        // applied here — it paints on an inset child layer below, because
        // backdrop-filter would make this element the containing block for the
        // tenant switcher's `fixed inset-0` click-away backdrop (which would
        // then cover only the sidebar column, breaking click-outside-to-close).
        'relative z-30 flex flex-col border-r transition-[width] duration-200 ease-in-out md:rounded-l-2xl',
        showExpanded ? 'w-64' : 'w-16'
      )}
      onMouseEnter={() => !expanded && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Frosted layer — see the note on the wrapper above: the blur must NOT
          sit on the rail element itself or it becomes the containing block for
          the tenant switcher's viewport-anchored click-away backdrop. */}
      <div className="beskar-glass pointer-events-none absolute inset-0 -z-10 md:rounded-l-2xl" aria-hidden="true" />

      {/* Brand bar */}
      <div className={cn('flex h-16 items-center border-b', showExpanded ? 'px-6' : 'justify-center px-2')}>
        {showExpanded ? (
          // Expanded: the full lockup. Collapsed (below) keeps the square
          // Logo, which is the only mark that fits a 64px rail.
          <Wordmark size="sm" className="min-w-0" />
        ) : (
          <Logo size="md" />
        )}
      </div>

      {/* Tenant/Org switcher — hidden when collapsed */}
      {showSwitcher && showExpanded && (
        <div className="relative border-b px-3 py-2">
          <button
            onClick={() => setSwitcherOpen((o) => !o)}
            disabled={isSwitching}
            data-tour-id="tenant-name"
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:opacity-50"
          >
            <div className={cn('flex h-6 w-6 flex-shrink-0 items-center justify-center rounded', currentIsPlatform ? 'bg-indigo-100 dark:bg-indigo-900/40' : 'bg-primary-100')}>
              {currentIsPlatform ? (
                <Shield className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
              ) : (
                <Building2 className="h-3.5 w-3.5 text-primary-600" />
              )}
            </div>
            <span className="flex-1 truncate font-medium text-gray-800">
              {currentIsPlatform ? 'Platform' : (user?.tenantName || 'Organization')}
            </span>
            {isSwitching ? (
              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400" />
            ) : (
              <ChevronDown className={cn('h-4 w-4 flex-shrink-0 text-gray-500 transition-transform', switcherOpen && 'rotate-180')} />
            )}
          </button>

          {switcherOpen && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setSwitcherOpen(false)} />
              {/* Dropdown */}
              <div className="absolute left-3 right-3 top-full z-20 mt-1 rounded-md border border-gray-200 bg-white shadow-lg">
                <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Switch Context
                </p>
                {tenantMemberships.map((membership) => {
                  const memberIsPlatform = membership.isPlatform || isPlatformRole(membership.role)
                  const isCurrent = memberIsPlatform
                    ? !!currentIsPlatform
                    : user?.tenantId === membership.tenantId && !currentIsPlatform

                  return (
                    <button
                      key={memberIsPlatform ? `platform-${membership.role}` : membership.tenantId}
                      onClick={() => !isCurrent && handleSwitchTenant(membership.tenantId, membership.tenantName, memberIsPlatform)}
                      disabled={isCurrent}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:cursor-default"
                    >
                      <div className={cn('flex h-6 w-6 flex-shrink-0 items-center justify-center rounded', memberIsPlatform ? 'bg-indigo-50 dark:bg-indigo-950/40' : 'bg-primary-50')}>
                        {memberIsPlatform ? (
                          <Shield className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                        ) : (
                          <Building2 className="h-3.5 w-3.5 text-primary-600" />
                        )}
                      </div>
                      <span className="flex-1 truncate text-gray-800">
                        {memberIsPlatform ? 'Platform Admin' : membership.tenantName}
                      </span>
                      {isCurrent && <Check className="h-4 w-4 flex-shrink-0 text-primary-600" />}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* layoutScroll: this element scrolls, and Framer measures layout in
          viewport coordinates. Without it, the active-marker slide is offset by
          the scroll distance whenever the nav is scrolled away from the top. */}
      <motion.nav
        layoutScroll
        aria-label="Main navigation"
        data-tour-id="sidebar-nav"
        className={cn('flex-1 overflow-y-auto', showExpanded ? 'p-4' : 'p-2')}
      >
        {sections.map((section, sectionIdx) => {
          const isCollapsed = showExpanded && !!collapsedSections[section.name]
          const showHeader = showExpanded && section.name !== '__ungrouped'

          return (
            <div key={section.name} className={sectionIdx > 0 ? 'mt-1' : ''}>
              {/* Section header — button for keyboard-accessible collapse */}
              {showHeader && (
                <div className={cn(sectionIdx > 0 ? 'mt-4' : '', 'mb-1')}>
                  <button
                    onClick={() => toggleSection(section.name)}
                    aria-expanded={!isCollapsed}
                    className="flex w-full items-center justify-between rounded px-3 py-0.5 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                  >
                    <span
                      className="text-[11px] font-semibold uppercase tracking-wider text-gray-400"
                      title={
                        section.name === 'Curate'
                          ? "Curate organizes your tenant’s favorites, tags, categories, and templates — curation artifacts that help you discover and surface resources without affecting how they run."
                          : undefined
                      }
                    >
                      {section.name}
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-3 w-3 text-gray-300 transition-transform duration-200',
                        isCollapsed && '-rotate-90'
                      )}
                    />
                  </button>
                </div>
              )}

              {/* Section divider in collapsed/rail mode */}
              {!showExpanded && sectionIdx > 0 && (
                <div className="my-3 flex justify-center">
                  <div className="h-px w-8 bg-gray-200" />
                </div>
              )}

              {/* Collapsible content — CSS grid transition (0fr ↔ 1fr) */}
              {!isCollapsed && (
                <div className="animate-in fade-in duration-150">
                  <div className="space-y-0.5">
                    {section.subSections.map((subSection) => (
                      <div key={subSection.name ?? '__default'}>
                        {/* Sub-section divider — only in expanded mode */}
                        {showExpanded && subSection.name && (
                          <div className="mt-2 mb-0.5 px-3">
                            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-300">
                              — {subSection.name} —
                            </span>
                          </div>
                        )}

                        {subSection.items.map((item) => (
                          <SidebarNavLink
                            key={item.to}
                            item={item}
                            showExpanded={showExpanded}
                            animateMarker={!prefersReducedMotion}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </motion.nav>

      {/* Footer-pinned nav (Deleted) — sits directly above the collapse control.
          Rendered through SidebarNavLink, the same component the scrolling list
          uses, so it shares the active marker's layoutId: the highlight slides
          down here from a list item instead of snapping. Outside <motion.nav>,
          so it never scrolls away and is never clipped by a collapsed section. */}
      {footerItems.length > 0 && (
        <div className={cn('space-y-0.5 border-t', showExpanded ? 'px-4 py-2' : 'px-2 py-2')}>
          {footerItems.map((item) => (
            <SidebarNavLink
              key={item.to}
              item={item}
              showExpanded={showExpanded}
              animateMarker={!prefersReducedMotion}
            />
          ))}
        </div>
      )}

      {/* Toggle button */}
      <div className={cn('border-t', showExpanded ? 'px-4 py-2' : 'px-2 py-2')}>
        <button
          onClick={toggleSidebar}
          className="flex w-full items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          title={expanded ? 'Collapse sidebar (Ctrl+B)' : 'Expand sidebar (Ctrl+B)'}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {expanded ? (
            <>
              <PanelLeftClose className="h-4 w-4" />
              <span className="ml-2 text-xs text-gray-400">Ctrl+B</span>
            </>
          ) : (
            <PanelLeftOpen className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Role indicator footer */}
      <div className={cn('border-t', showExpanded ? 'p-4' : 'p-2')}>
        {showExpanded ? (
          <div className="text-xs text-gray-500">
            <div>Version 0.5.1</div>
            <div className="mt-1">Stage 2 - Feature Development</div>
            {persona.role && (
              <div className="mt-2 inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                {persona.roleLabel}
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate('/oss-licenses')}
              className="mt-2 block text-left text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline dark:text-gray-500 dark:hover:text-gray-300"
            >
              Open Source Licenses
            </button>
          </div>
        ) : (
          <div className="flex justify-center" title={`v0.5.1 · ${persona.roleLabel}`}>
            <span className="text-[10px] text-gray-400">v0.5</span>
          </div>
        )}
      </div>
    </div>
  )
}
